/**
 * LA PROJECTION D'UNE MUTATION — extraite de `affaire.tsx` le 24/09/2026.
 *
 * ⚠️ POURQUOI UN MODULE, ET PAS UNE SECONDE COPIE. La fiche parcelle doit
 * montrer ce qu'une mutation en cours fera au fonds ; `affaire.tsx` le montrait
 * déjà, dans une fonction LOCALE. En écrire une seconde pour la parcelle aurait
 * donné deux implémentations d'une même règle : elles divergent au premier
 * correctif appliqué à une seule. Le correctif `?? 9` du tri ci-dessous, posé
 * sous verrou de déploiement le 21/09, est exactement le genre de rustine qui
 * n'aurait atteint qu'une des deux.
 *
 * ⚠️ ET LE DIAGNOSTIC QUI M'Y A MENÉ ÉTAIT FAUX. L'item D.933 annonçait « le
 * volet impact existe, il suffit de câbler `effet.tsx` ». Mesuré : `effet.tsx`
 * est `CompositeurEffet`, un formulaire de SAISIE — pas un afficheur d'impact.
 * Le moteur est `projeterMutation()`, servi par `GET /mutations/:ref/projection`.
 * Chercher le volet sur le nom du fichier au lieu de la route l'avait désigné
 * à côté.
 *
 * AUCUN CALCUL N'EST FAIT ICI : le service applique l'effet pour de vrai dans
 * une transaction qu'il annule. Recalculer « ce qui va se passer » côté
 * navigateur produirait un écran qui annonce vert quand la validation échoue —
 * ou l'inverse, qui est pire, parce que l'agent corrige alors un dossier sain.
 */
import { useEffect, useState } from 'react';
import { api, ErreurApi, type DroitProjete, type Projection } from './api';

/**
 * CE QUE CET ACTE FERA AU REGISTRE — §62 · §63 · §64 · §77.
 *
 * ⚠️ LE PRINCIPE VIENT DU FONDATEUR (21/09/2026) : « une mutation n'est pas un
 * seul formulaire, mais un ensemble d'écrans reliés par un espace qui calcule
 * l'avant, l'opération et l'après ». Cette section est cette vue-là.
 *
 * ⚠️ AUCUN CALCUL N'EST FAIT ICI. L'écran affiche ce que le service a projeté
 * en APPLIQUANT l'effet pour de vrai, puis en annulant. Recalculer côté
 * navigateur « ce qui va se passer » aurait produit la divergence la plus chère
 * de ce dépôt : un écran qui annonce vert et une validation qui échoue — ou
 * l'inverse, qui est pire, parce que l'agent corrige alors un dossier sain.
 *
 * ⚠️ ELLE SE CHARGE UNE SEULE FOIS, EN DIFFÉRÉ. Le service prend, le temps de
 * la simulation, les MÊMES verrous qu'une écriture : recharger à chaque rendu
 * mettrait une consultation en concurrence avec les actes réels.
 */
export function SectionProjection({ reference, titre = "Ce que cet acte fera au registre" }: {
  reference: string;
  /** La fiche parcelle en affiche une PAR mutation en cours : répéter « Ce que
   *  cet acte fera » quatre fois de suite ne dirait plus de quel acte il s'agit. */
  titre?: string;
}) {
  const [p, setP] = useState<Projection | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(true);

  useEffect(() => {
    let vivant = true;
    setOccupe(true); setErreur(null); setP(null);
    api.mutations.projection(reference)
      .then((r) => { if (vivant) setP(r); })
      .catch((e) => {
        if (!vivant) return;
        setErreur(e instanceof ErreurApi && e.estRefus
          ? "Votre habilitation ne couvre pas l'état des droits du fonds (fon.droit:lire) : "
            + "la projection nomme les titulaires, elle ne vous est pas montrée."
          : String((e as Error).message ?? e));
      })
      .finally(() => { if (vivant) setOccupe(false); });
    return () => { vivant = false; };
  }, [reference]);

  return (
    <section>
      <h4>{titre}</h4>

      {occupe && (
        <p className="vide">
          Projection en cours — le service applique l'effet dans une transaction qu'il annule,
          pour dire exactement ce que la validation produira.
        </p>
      )}
      {erreur && <p className="note-indispo">{erreur}</p>}

      {p?.deja_appliquee && <p className="vide">{p.lecture}</p>}

      {p && !p.deja_appliquee && p.jouable === false && (
        <div className="avertissement danger">
          <strong>Cet acte n'est pas applicable en l'état.</strong>
          <p className="proj-refus">{p.refus}</p>
          <p className="proj-refus">
            Le corriger maintenant évite de bloquer le fonds : une mutation approuvée dont
            l'effet échoue reste ouverte, et aucune autre ne peut plus être déposée sur cette parcelle.
          </p>
        </div>
      )}

      {p && !p.deja_appliquee && p.jouable && (
        <>
          <div className="proj-colonnes">
            <ColonneProjection titre="Avant" droits={p.avant ?? []}
              vide="Aucun droit inscrit — le fonds n'est pas encore au registre." />
            <div className="proj-colonne">
              <h5 className="proj-tete">Opération</h5>
              <ul className="liste-fine">
                <li><strong>{p.operation?.radies.length ?? 0}</strong> droit(s) radié(s)</li>
                <li><strong>{p.operation?.inscrits.length ?? 0}</strong> droit(s) inscrit(s)</li>
                <li><strong>{p.operation?.survivants.length ?? 0}</strong> droit(s) inchangé(s)</li>
              </ul>
              {p.quotites && (
                <p className="proj-quotites">
                  Propriété : {p.quotites.avant.parts === 0 ? '—' : `${p.quotites.avant.num}/${p.quotites.avant.den}`}
                  {' → '}
                  {p.quotites.apres.parts === 0 ? '—' : `${p.quotites.apres.num}/${p.quotites.apres.den}`}
                </p>
              )}
            </div>
            <ColonneProjection titre="Après" droits={p.apres ?? []}
              vide="Aucun droit ne subsisterait sur ce fonds." />
          </div>

          {/* ⚠️ TRIÉS PAR GRAVITÉ, et non dans l'ordre où le service les a
              produits : une charge qui survit doit se lire avant la liste des
              écritures ordinaires. Le tri est ici parce qu'il est d'affichage ;
              les NIVEAUX, eux, viennent du service. */}
          <ul className="proj-impacts">
            {[...(p.impacts ?? [])]
              // ⚠️ `?? 9` : `Record<string, number>` indexé par une chaîne rend
              // `number | undefined` — deux erreurs TS2532 ici, qui bloquaient
              // le typecheck du déployeur. Un niveau INCONNU du barème tombe en
              // fin de tri, jamais en tête : le rang par défaut doit être le
              // moins alarmant, sinon une valeur non reconnue se hisserait
              // au-dessus des bloquants. Correctif minimal posé par
              // `cadastral-os-notarial` le 21/09 sous verrou de déploiement ;
              // la logique de tri est inchangée.
              .sort((a, b) => (RANG_IMPACT[a.niveau] ?? 9) - (RANG_IMPACT[b.niveau] ?? 9))
              .map((i) => (
                <li key={i.code} className={`proj-impact ${i.niveau}`}>{i.texte}</li>
              ))}
          </ul>

          <p className="proj-lecture">{p.lecture}</p>
        </>
      )}
    </section>
  );
}

const RANG_IMPACT: Record<string, number> = { bloquant: 0, eleve: 1, attention: 2, info: 3 };

function ColonneProjection({ titre, droits, vide }: {
  titre: string; droits: DroitProjete[]; vide: string;
}) {
  return (
    <div className="proj-colonne">
      <h5 className="proj-tete">{titre}</h5>
      {droits.length === 0 ? <p className="vide">{vide}</p> : (
        <ul className="liste-fine">
          {droits.map((d) => (
            <li key={d.id}>
              <span className="proj-nature">{d.nature}</span>{' '}
              {d.quotite.num}/{d.quotite.den}
              {d.rang !== null ? ` · rang ${d.rang}` : ''}
              <br />{d.titulaire}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
